// Story 213 — the workstation webcam (CU4, CU5, V1, V2). Frames go to Sun only, as JPEG in base64 without
// the `data:` prefix, at most 640 px on the longer side. Images are never stored nor logged (RN31).

const MAX_FRAME_SIDE = 640
const JPEG_QUALITY = 0.7

// V2: the camera failure codes Sun accepts (CameraFailure).
const FAILURE_BY_ERROR_NAME = {
  NotAllowedError: 'PERMISSION_DENIED',
  SecurityError: 'PERMISSION_DENIED',
  NotFoundError: 'NOT_FOUND',
  OverconstrainedError: 'NOT_FOUND',
  NotReadableError: 'NOT_READABLE',
  AbortError: 'NOT_READABLE'
}

export const cameraFailureOf = (error) => FAILURE_BY_ERROR_NAME[error && error.name] || 'NOT_READABLE'

export const frameSize = (width, height) => {
  const scale = Math.min(1, MAX_FRAME_SIDE / Math.max(width, height))
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

/**
 * Opens the webcam. Resolves to `{ failure: '<code>' }` when it cannot (getUserMedia needs a secure
 * context, like the QR scanner), or to `{ failure: '', camera }`, where `camera.failure()` tells whether
 * the track ended since (webcam unplugged ⇒ ENDED).
 */
export const openVisionCamera = async () => {
  if (!window.isSecureContext || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { failure: 'UNSUPPORTED' }
  }
  let stream
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
  } catch (error) {
    return { failure: cameraFailureOf(error) }
  }
  const [track] = stream.getVideoTracks()
  let ended = !track || track.readyState === 'ended'
  if (track) {
    track.addEventListener('ended', () => { ended = true })
  }
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.srcObject = stream
  const canvas = document.createElement('canvas')
  let playing = false

  const stop = () => {
    stream.getTracks().forEach((streamTrack) => streamTrack.stop())
    video.srcObject = null
  }

  const capture = async () => {
    if (!playing) {
      await video.play()
      playing = true
    }
    const settings = track.getSettings ? track.getSettings() : {}
    const size = frameSize(video.videoWidth || settings.width || MAX_FRAME_SIDE, video.videoHeight || settings.height || MAX_FRAME_SIDE)
    canvas.width = size.width
    canvas.height = size.height
    canvas.getContext('2d').drawImage(video, 0, 0, size.width, size.height)
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
    return dataUrl.slice(dataUrl.indexOf(',') + 1)
  }

  return {
    failure: '',
    camera: {
      failure: () => (ended ? 'ENDED' : ''),
      capture,
      stop
    }
  }
}
